import { useState } from 'react';
import { ChefHat, Import, Calendar, ShoppingCart, AlertCircle, Users, Share2, Heart, X, MessageSquare, Bookmark, TrendingUp } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Logo from './Logo';
import ProfileSetup from './ProfileSetup';

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{email?: string; password?: string}>({});
  const { signIn, signUp, resetPassword } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [tempUser, setTempUser] = useState<any>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const emailError = validateEmail(email);
    const passwordError = !isForgotPassword ? validatePassword(password) : '';

    setErrors({
      email: emailError,
      password: passwordError
    });

    if (emailError || passwordError) return;

    try {
      setLoading(true);
      setErrors({});

      if (isForgotPassword) {
        await resetPassword(email);
        setIsForgotPassword(false);
        return;
      }

      if (isSignUp) {
        const { data } = await signUp(email, password);
        if (data?.user) {
          setTempUser(data.user);
          setShowProfileSetup(true);
          setShowAuthModal(false);
        }
      } else {
        await signIn(email, password);
      }
    } catch (error: any) {
      if (error.message?.toLowerCase().includes('already registered')) {
        setIsSignUp(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAuthModal = (signup: boolean = false) => {
    setIsSignUp(signup);
    setIsForgotPassword(false);
    setEmail('');
    setPassword('');
    setErrors({});
    setShowAuthModal(true);
  };

  if (showProfileSetup && tempUser) {
    return (
      <ProfileSetup
        user={tempUser}
        onComplete={() => {
          setShowProfileSetup(false);
          setTempUser(null);
          handleOpenAuthModal(false);
        }}
        onCancel={() => {
          setShowProfileSetup(false);
          setTempUser(null);
          handleOpenAuthModal(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Mobile-optimized header */}
      <header className="bg-white/80 backdrop-blur-sm sticky top-0 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <Logo />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => handleOpenAuthModal(false)}
              className="text-sm"
            >
              Sign In
            </Button>
            <Button
              onClick={() => handleOpenAuthModal(true)}
              className="text-sm"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-600 text-white px-4 py-12 md:py-24">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-primary-50">
            Your Personal Kitchen Assistant
          </h1>
          <p className="text-lg md:text-xl mb-8 text-primary-50">
            Store, organize, and discover recipes. Plan your meals and create shopping lists.
          </p>
          <Button
            size="lg"
            className="w-full md:w-auto bg-white text-primary-600 hover:bg-primary-50"
            onClick={() => handleOpenAuthModal(true)}
          >
            Get Started Free
          </Button>
        </div>
      </div>

      {/* Features Grid */}
      <div className="px-4 py-12 md:py-24">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-gray-900">
          Everything You Need
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto">
          <div className="aspect-square bg-white rounded-2xl p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-3">
              <ChefHat className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold mb-1">Recipe Management</h3>
            <p className="text-xs md:text-sm text-gray-600">Store all your recipes</p>
          </div>
          
          <div className="aspect-square bg-white rounded-2xl p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-3">
              <Import className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold mb-1">Recipe Import</h3>
            <p className="text-xs md:text-sm text-gray-600">Import from any website</p>
          </div>

          <div className="aspect-square bg-white rounded-2xl p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-3">
              <Calendar className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold mb-1">Meal Planning</h3>
            <p className="text-xs md:text-sm text-gray-600">Plan your meals ahead</p>
          </div>

          <div className="aspect-square bg-white rounded-2xl p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-3">
              <ShoppingCart className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold mb-1">Shopping Lists</h3>
            <p className="text-xs md:text-sm text-gray-600">Auto-generate lists</p>
          </div>
        </div>
      </div>

      {/* Community Section */}
      <div className="bg-white px-4 py-12 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Join Our Community
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with passionate home cooks, share recipes, and discover new dishes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-2xl">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <Share2 className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Share Recipes</h3>
              <p className="text-gray-600 mb-4">
                Share your favorite recipes and discover unique dishes from other cooks.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Like recipes
                </span>
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Comment
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-2xl">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Build Your Network</h3>
              <p className="text-gray-600 mb-4">
                Follow other cooks and grow your cooking community.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Follow cooks
                </span>
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Get updates
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-2xl">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Discover & Learn</h3>
              <p className="text-gray-600 mb-4">
                Find trending recipes and learn from the community.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Trending recipes
                </span>
                <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm">
                  Save favorites
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {isForgotPassword
                  ? 'Reset Password'
                  : isSignUp
                  ? 'Create your account'
                  : 'Welcome back'}
              </h2>
              <p className="text-gray-600 mt-2">
                {isForgotPassword
                  ? "Enter your email and we'll send you reset instructions"
                  : isSignUp
                  ? 'Start organizing your recipes today'
                  : 'Sign in to access your recipes'}
              </p>
            </div>

            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: '' });
                    }}
                    placeholder="Enter your email"
                    error={!!errors.email}
                    disabled={loading}
                  />
                  {errors.email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                      <AlertCircle className="h-5 w-5" />
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {!isForgotPassword && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setErrors({ ...errors, password: '' });
                      }}
                      placeholder="Enter your password"
                      error={!!errors.password}
                      disabled={loading}
                    />
                    {errors.password && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                  )}
                  {isSignUp && (
                    <ul className="mt-2 text-sm text-gray-500 space-y-1">
                      <li className="flex items-center gap-1">
                        <span className={password.length >= 6 ? 'text-green-500' : ''}>•</span>
                        At least 6 characters
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[A-Z]/.test(password) ? 'text-green-500' : ''}>•</span>
                        One uppercase letter
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[0-9]/.test(password) ? 'text-green-500' : ''}>•</span>
                        One number
                      </li>
                    </ul>
                  )}
                </div>
              )}

              {!isSignUp && !isForgotPassword && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setErrors({});
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                isLoading={loading}
              >
                {isForgotPassword
                  ? 'Send Reset Instructions'
                  : isSignUp
                  ? 'Create Account'
                  : 'Sign In'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    if (isForgotPassword) {
                      setIsForgotPassword(false);
                    } else {
                      setIsSignUp(!isSignUp);
                    }
                    setErrors({});
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {isForgotPassword
                    ? 'Back to sign in'
                    : isSignUp
                    ? 'Already have an account? Sign in'
                    : "Don't have an account? Sign up"}
                </button>
              </div>
            </form>

            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}